import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { TWclassNames } from "./Div";

export const PMTabs = (props) => {
  const { selectedTab, setSelectedTab, maxVisibleTabs = 6 } = props;
  const [showMoreTabs, setShowMoreTabs] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  // Keep original tabs array for correct indexing
  const allTabs = props.tabs;
  const activeTabs = allTabs.filter((tab) => tab.active !== false);
  const visibleTabs = activeTabs.slice(0, maxVisibleTabs);
  const moreTabs = activeTabs.slice(maxVisibleTabs);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (showMoreTabs && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 224, // 224px = w-56 (14rem)
      });
    }
  }, [showMoreTabs]);

  return (
    <div>
      <div className="sm:hidden">
        <label htmlFor="tabs" className="sr-only">
          Select a tab
        </label>
        {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
        <select
          onChange={(e) => setSelectedTab(e?.target?.value)}
          id="tabs"
          name="tabs"
          className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          defaultValue={allTabs[selectedTab]?.name}
        >
          {activeTabs.map((tab, tabIdx) => {
            const actualIndex = allTabs.findIndex(t => t === tab);
            return (
              <option key={tab?.name} value={actualIndex}>
                {tab?.name}
              </option>
            );
          })}
        </select>
      </div>
      <div className="hidden sm:block">
        <nav
          className="isolate flex divide-x divide-gray-200 rounded-lg shadow overflow-visible"
          aria-label="Tabs"
        >
          {/* Visible tabs */}
          {visibleTabs.map((tab, tabIdx) => {
            const actualIndex = allTabs.findIndex(t => t === tab);
            return (
              <a
                key={tab.name}
                onClick={() => setSelectedTab(actualIndex)}
                className={TWclassNames(
                  actualIndex === selectedTab
                    ? "text-gray-900"
                    : "text-gray-500 hover:text-gray-700",
                  tabIdx === 0 ? "rounded-l-lg" : "",
                  moreTabs.length === 0 && tabIdx === visibleTabs.length - 1 ? "rounded-r-lg" : "",
                  "flex items-center justify-center cursor-pointer group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10"
                )}
                aria-current={actualIndex === selectedTab ? "page" : undefined}
              >
                {tab?.icon && <tab.icon className="h-5 w-5 mr-2" />}
                <span className="truncate">{tab.name}</span>
                <span
                  aria-hidden="true"
                  className={TWclassNames(
                    actualIndex === selectedTab ? "bg-blue-500" : "bg-transparent",
                    "absolute inset-x-0 bottom-0 h-0.5"
                  )}
                />
              </a>
            );
          })}

          {/* More tabs dropdown */}
          {moreTabs.length > 0 && (
            <div className="relative z-50">
              <button
                ref={buttonRef}
                onClick={() => setShowMoreTabs(!showMoreTabs)}
                className={TWclassNames(
                  "flex items-center justify-center cursor-pointer group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10 rounded-r-lg",
                  showMoreTabs ? "text-gray-900" : "text-gray-500 hover:text-gray-700"
                )}
              >
                <span className="mr-2">Flere</span>
                <ChevronDownIcon className="h-4 w-4" />
              </button>

              {/* Dropdown menu - removed inline version */}
            </div>
          )}
        </nav>

      </div>

      {/* Portal-based dropdown menu */}
      {showMoreTabs && createPortal(
        <>
          {/* Click outside overlay */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowMoreTabs(false)}
          />
          
          {/* Dropdown menu */}
          <div 
            className="fixed z-50 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            <div className="py-1">
              {moreTabs.map((tab, tabIdx) => {
                const actualIndex = allTabs.findIndex(t => t === tab);
                return (
                  <button
                    key={tab.name}
                    onClick={() => {
                      setSelectedTab(actualIndex);
                      setShowMoreTabs(false);
                    }}
                    className={TWclassNames(
                      actualIndex === selectedTab
                        ? "bg-gray-100 text-gray-900"
                        : "text-gray-700 hover:bg-gray-50",
                      "group flex w-full items-center px-4 py-2 text-sm"
                    )}
                  >
                    {tab?.icon && <tab.icon className="h-5 w-5 mr-3" />}
                    {tab.name}
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
